import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * Connected platform accounts with OAuth tokens or user-provided credentials.
 * Tokens and credentials are encrypted at rest.
 */
@Entity('recon_platform_accounts')
@Index('idx_recon_platform_accounts_platform', ['platformId'])
@Index('idx_recon_platform_accounts_status', ['status'])
@Index('idx_recon_platform_accounts_token_expires', ['tokenExpiresAt'])
export class ReconPlatformAccountEntity extends BaseEntity {
	/**
	 * Platform identifier.
	 * 'twitter', 'reddit', 'bluesky', 'linkedin', 'youtube', 'discord', 'mastodon'
	 */
	@Column('varchar', { name: 'platform_id', length: 50 })
	public platformId!: string;

	@Column('varchar', {
		name: 'platform_account_id',
		length: 255,
		nullable: true,
	})
	public platformAccountId!: string | null;

	@Column('varchar', {
		name: 'platform_display_name',
		length: 255,
		nullable: true,
	})
	public platformDisplayName!: string | null;

	@Column('varchar', { name: 'platform_handle', length: 255, nullable: true })
	public platformHandle!: string | null;

	@Column('text', { name: 'platform_profile_url', nullable: true })
	public platformProfileUrl!: string | null;

	/**
	 * Encrypted credentials JSON (for non-OAuth platforms like Bluesky).
	 * Twitter: { apiKey, apiSecret, accessToken, accessTokenSecret } (v1.1 API)
	 * Reddit: { clientId, clientSecret, refreshToken }
	 * Bluesky: { handle, appPassword }
	 * LinkedIn: { accessToken, refreshToken }
	 * Mastodon: { instanceUrl, accessToken }
	 */
	@Column('text', { name: 'credentials_encrypted', nullable: true })
	public credentialsEncrypted!: string | null;

	/**
	 * Authentication method used for this account.
	 * 'oauth2' - Standard OAuth 2.0 flow
	 * 'oauth2_pkce' - OAuth 2.0 with PKCE (Twitter)
	 * 'app_password' - App password (Bluesky)
	 * 'api_key' - API key (legacy Twitter v1.1)
	 * 'bot_token' - Bot token (Discord)
	 */
	@Column('varchar', { name: 'auth_method', length: 20, default: 'oauth2' })
	public authMethod!: string;

	/**
	 * OAuth access token (encrypted).
	 * Used for OAuth-based platforms.
	 */
	@Column('text', { name: 'access_token_encrypted', nullable: true })
	public accessTokenEncrypted!: string | null;

	/**
	 * OAuth refresh token (encrypted).
	 * Used to obtain new access tokens before expiry.
	 */
	@Column('text', { name: 'refresh_token_encrypted', nullable: true })
	public refreshTokenEncrypted!: string | null;

	/**
	 * When the access token expires.
	 * Used by the token refresh service to proactively refresh tokens.
	 */
	@Column('datetime', { name: 'token_expires_at', nullable: true })
	public tokenExpiresAt!: Date | null;

	/**
	 * OAuth scopes granted by the user.
	 * Stored as comma-separated string.
	 */
	@Column('text', { name: 'oauth_scopes', nullable: true })
	public oauthScopes!: string | null;

	/**
	 * Account status.
	 * 'active', 'expired', 'revoked', 'error', 'disconnected'
	 */
	@Column('varchar', { name: 'status', length: 20, default: 'active' })
	public status!: string;

	@Column('text', { name: 'last_error', nullable: true })
	public lastError!: string | null;

	/**
	 * Rate limit tracking per endpoint.
	 * Initialize to {} when creating new records.
	 */
	@Column('json', { name: 'rate_limit_state', nullable: true })
	public rateLimitState!: Record<string, unknown> | null;

	@Column('datetime', { name: 'last_sync_at', nullable: true })
	public lastSyncAt!: Date | null;

	/**
	 * When the token was last refreshed.
	 */
	@Column('datetime', { name: 'last_token_refresh_at', nullable: true })
	public lastTokenRefreshAt!: Date | null;
}
